import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * UniversalAppFooter — single-line corporate footer for the authenticated
 * app shell. Sits at the bottom of `UniversalAppLayout` and shows the legal
 * copyright + registration number on top of a darkened hero-image background
 * (matches the top nav for a coherent shell look).
 *
 * `mt-auto` + `shrink-0` pin it to the very bottom of the flex column.
 */
interface UniversalAppFooterProps {
  legalName: string;
  registration?: string;
  /** Override the displayed year. Defaults to the current calendar year. */
  year?: number;
  /** Optional left-side content (e.g. version, quick links). */
  left?: React.ReactNode;
  /** Optional right-side content (e.g. status indicator). */
  right?: React.ReactNode;
  /** Hero image URL used as a darkened background. Defaults to
   *  `/images/hero-corporate-main.png`. Pass `null` to fall back to flat fill. */
  backgroundImage?: string | null;
  className?: string;
}

const DEFAULT_HERO = '/images/hero-corporate-main.png';

export function UniversalAppFooter({
  legalName,
  registration,
  year = new Date().getFullYear(),
  left,
  right,
  backgroundImage = DEFAULT_HERO,
  className,
}: UniversalAppFooterProps) {
  return (
    <footer
      role="contentinfo"
      className={cn(
        'mt-auto shrink-0 relative flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 border-t border-black/30 bg-neutral-900 px-6 py-3 text-xs text-white/80 overflow-hidden',
        className
      )}
    >
      {backgroundImage && (
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${backgroundImage}')` }}
        />
      )}
      {backgroundImage && (
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/55"
        />
      )}
      <div className="relative z-10 flex items-center gap-3">
        {left}
        <span>
          &copy; {year} {legalName}. All rights reserved.
        </span>
      </div>
      <div className="relative z-10 flex items-center gap-3">
        {registration && <span>Registration Number: {registration}</span>}
        {right}
      </div>
    </footer>
  );
}

export type { UniversalAppFooterProps };
